import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import DropdownActions from "../../../../parts/dropdown-actions";
import SvgIcon from "../../../../parts/SvgIcon";
import Toast from "../../../../parts/toast";
import CustomizeNutritionTableModal from "./views/CustomizeNutritionTableModal";

const EDIT_PENCIL_ICON = "/img/new-icons/edit-pencil.svg";

// "Customise Table" — dropdown (Default View + saved templates, each with
// its own edit icon via DropdownActions) + "Customize Table" button (always
// opens the modal in CREATE mode) + the modal itself, which the dropdown's
// per-row edit icon can also open in EDIT mode (see handleEditTemplate).
// Which template is active is a CONTROLLED prop
// (activeTemplateId/onActiveTemplateChange) — it used to be local state
// here, but TableView.js now needs to read it too (to filter columns), so
// the parent (NutritionProgress.js) owns it. Modal-open state and which
// template is being edited (null = creating) stay local. Renders nothing
// off the Table tab.
const TableTemplateControls = ({
  activeView,
  templates,
  onTemplateSaved,
  onTemplateDeleted,
  intensityMeasure,
  activeTemplateId,
  onActiveTemplateChange,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Client-side stand-in for the server-driven flash() Toast (page.js reads
  // locals.flash() at SSR render time, so it only works after a full page
  // reload) — this save is a fetch-without-reload, same situation as
  // SchedulingSettings.js, which sets this same precedent.
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  /**
   * Cleanup the toast timer when the component unmounts.
   */
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  if (activeView !== "table") return null;

  // Shows the toast message
  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type: "success", message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  /**
   * Creates a new template (editingTemplate === null) or updates the one
   * being edited — a single POST either way (Rill actions only support
   * .post(), same as challenge-report-templates.js's own create/update
   * route), branching on whether `id` is in the body server-side.
   */
  const handleSaveTemplate = async ({ name, columns }) => {
    const isEdit = Boolean(editingTemplate);
    const res = await fetch("/actions/nutrition-table-templates", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(
        isEdit ? { id: editingTemplate._id, name, columns } : { name, columns }
      ),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          t(`nutritionPage.progress.tableTemplate.errors.${isEdit ? "updateFailed" : "saveFailed"}`)
      );
    }
    onTemplateSaved(data.template);
    onActiveTemplateChange(String(data.template._id));
    setEditingTemplate(null);
    showToast(
      t(`nutritionPage.progress.tableTemplate.${isEdit ? "updateSuccess" : "saveSuccess"}`)
    );
  };

  /**
   * Deletes the template being edited. Clears activeTemplateId back to
   * "Default View" if the deleted template was the one currently applied.
   */
  const handleDeleteTemplate = async (id) => {
    const res = await fetch("/actions/nutrition-table-templates/remove", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          t("nutritionPage.progress.tableTemplate.errors.deleteFailed")
      );
    }
    onTemplateDeleted(id);
    if (String(activeTemplateId) === String(id)) onActiveTemplateChange(null);
    setEditingTemplate(null);
    showToast(t("nutritionPage.progress.tableTemplate.deleteSuccess"));
  };

  // Handles the edit template action
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  // Closes the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  return (
    <>
      <div className="np-table-controls">
        <DropdownActions
          summaryText={
            (activeTemplateId &&
              templates.find((tpl) => String(tpl._id) === activeTemplateId)?.name) ||
            t("nutritionPage.progress.tableTemplate.defaultView")
          }
          options={[
            { text: t("nutritionPage.progress.tableTemplate.defaultView"), value: "" },
            ...templates.map((tpl) => ({
              text: tpl.name,
              value: String(tpl._id),
              onEdit: () => handleEditTemplate(tpl),
              editLabel: t("nutritionPage.progress.tableTemplate.editTemplate"),
            })),
          ]}
          onSelectFn={(value) => onActiveTemplateChange(value || null)}
        />
        <button
          type="button"
          data-button-type="primary"
          className="np-table-controls__customize-btn"
          onClick={() => {
            setEditingTemplate(null);
            setIsModalOpen(true);
          }}
        >
          <SvgIcon
            src={EDIT_PENCIL_ICON}
            noFilterOnLight
            classNames="np-table-controls__customize-icon"
          />
          {t("nutritionPage.progress.tableTemplate.customizeTable")}
        </button>
      </div>

      <CustomizeNutritionTableModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveTemplate}
        onDelete={handleDeleteTemplate}
        editingTemplate={editingTemplate}
        intensityMeasure={intensityMeasure}
      />

      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "success"}
        message={toast?.message ?? ""}
      />
    </>
  );
};

TableTemplateControls.propTypes = {
  activeView: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onTemplateSaved: PropTypes.func.isRequired,
  onTemplateDeleted: PropTypes.func.isRequired,
  intensityMeasure: PropTypes.string,
  activeTemplateId: PropTypes.string,
  onActiveTemplateChange: PropTypes.func.isRequired,
};

export default TableTemplateControls;
